import { useMemo } from 'react';
import { useConfigs } from '@features/configs';
import { buildGraph, GraphData } from '../utils/buildGraph';

/**
 * Hook that builds the journey graph for a given DNIS entry point.
 * Returns React Flow nodes and edges positioned by Dagre layout.
 */
export function useJourneyGraph(startConfigId: string | null): {
  graphData: GraphData | null;
  isLoading: boolean;
  dnisConfigs: { configId: string; configName: string }[];
} {
  const { data, isLoading } = useConfigs();
  const allConfigs = data?.items ?? [];

  // Get all DNIS configs for the selector
  const dnisConfigs = useMemo(
    () =>
      allConfigs
        .filter((c) => c.configType === 'dnis' && !c.deletionScheduledAt)
        .map((c) => ({ configId: c.configId, configName: c.configName }))
        .sort((a, b) => a.configName.localeCompare(b.configName)),
    [allConfigs]
  );

  // Build graph when a start config is selected
  const graphData = useMemo(() => {
    if (!startConfigId || allConfigs.length === 0) return null;
    return buildGraph(startConfigId, allConfigs);
  }, [startConfigId, allConfigs]);

  return { graphData, isLoading, dnisConfigs };
}
